import { Navigate, Outlet } from 'react-router';
import { NotFound, PageWrapper } from '@britishcouncil/react-admin';

import { ExaminerProfileBox, ProtectedExaminerPage, ProtectedPage } from 'components';
import { ROUTING_PATHS } from 'const';
import { BreadcrumbRouteObject } from 'types';
import { ExaminerRoleEnum, Permission } from 'api';
import { Certification } from './certification/Certification';
import { SpeakingCertificationList } from './certification/speaking/SpeakingCertificationList';
import { WritingCertificationList } from './certification/writing/WritingCertificationList';
import { LifeSkillsCertificationList } from './certification/life-skills/LifeSkillsCertificationList';
import { Monitoring } from './monitoring/Monitoring';
import { SpeakingMonitoringList } from './monitoring/speaking/SpeakingMonitoringList';
import { LifeSkillsMonitoringList } from './monitoring/life-skills/LifeSkillsMonitoringList';
import { EorSpeakingMonitoringList } from './monitoring/eor-speaking/EorSpeakingMonitoringList';
import { OnlineSpeakingMonitoringList } from './monitoring/online-speaking/OnlineSpeakingMonitoringList';
import { OsmMonitoringList } from './monitoring/osm/OsmMonitoringList';
import { OdpMonitoringList } from './monitoring/odp/OdpMonitoringList';
import { Centres } from './centres/Centres';

const { examiner, examiners } = ROUTING_PATHS;

export const EXAMINER_ROUTES: BreadcrumbRouteObject[] = [
  {
    path: examiners.add.path,
    breadcrumb: { text: 'Add new examiner', icon: 'plus' },
    lazy: () => import('./add-examiner'),
  },
  {
    path: examiner.path,
    breadcrumb: { text: 'Examiner Profile', icon: 'user' },
    element: (
      <PageWrapper>
        <ExaminerProfileBox />
        <Outlet />
      </PageWrapper>
    ),
    children: [
      {
        path: examiner.details.path,
        lazy: () => import('./examiner-details'),
      },
      {
        path: examiner.contactDetails.path,
        lazy: () => import('./contact-details'),
      },
      {
        path: examiner.centres.path,
        breadcrumb: { text: 'Centre Affiliation', icon: 'building' },
        element: (
          <ProtectedPage {...examiner.centres}>
            <Outlet />
          </ProtectedPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={examiner.centres.speaking.path} replace />,
          },
          {
            path: examiner.centres.speaking.path,
            breadcrumb: { text: 'Speaking Centres', icon: 'comment outline' },
            element: <Centres />,
            children: [
              { index: true, element: <Navigate to="centres" replace /> },
              { path: 'centres', lazy: () => import('./centres/speaking') },
              { path: 'locations', lazy: () => import('./centres/speaking-locations') },
            ],
          },
          {
            path: examiner.centres.writing.path,
            breadcrumb: { text: 'Writing Centres', icon: 'pencil' },
            element: <Centres />,
            children: [
              { index: true, element: <Navigate to="centres" replace /> },
              { path: 'centres', lazy: () => import('./centres/writing') },
              { path: 'locations', lazy: () => import('./centres/writing-locations') },
            ],
          },
          {
            path: examiner.centres.lifeSkills.path,
            breadcrumb: { text: 'Life Skills Centres', icon: 'chart line' },
            element: <Centres />,
            children: [
              { index: true, element: <Navigate to="centres" replace /> },
              { path: 'centres', lazy: () => import('./centres/life-skills') },
              { path: 'locations', lazy: () => import('./centres/life-skills-locations') },
            ],
          },
        ],
      },
      {
        path: examiner.certification.path,
        breadcrumb: { text: 'Certification', icon: 'graduation cap' },
        abstract: true,
        children: [
          {
            element: (
              <ProtectedPage {...examiner.certification}>
                <Certification />
              </ProtectedPage>
            ),
            children: [
              { index: true, element: <Navigate to="speaking" replace /> },
              {
                path: examiner.certification.speaking.path,
                element: <SpeakingCertificationList />,
              },
              { path: examiner.certification.writing.path, element: <WritingCertificationList /> },
              {
                path: examiner.certification.lifeSkills.path,
                element: <LifeSkillsCertificationList />,
              },
            ],
          },
          {
            path: examiner.certification.speaking.path,
            abstract: true,
            children: [
              {
                path: examiner.certification.speaking.add.path,
                breadcrumb: { text: 'Add Speaking Certification', icon: 'plus' },
                lazy: () => import('./certification/speaking/speaking-certification-add'),
              },
              {
                path: examiner.certification.speaking.details.path,
                breadcrumb: { text: 'Speaking Certification Details', icon: 'comment outline' },
                lazy: () => import('./certification/speaking/speaking-certification-details'),
              },
            ],
          },
          {
            path: examiner.certification.writing.path,
            abstract: true,
            children: [
              {
                path: examiner.certification.writing.add.path,
                breadcrumb: { text: 'Add Writing Certification', icon: 'plus' },
                lazy: () => import('./certification/writing/writing-certification-add'),
              },
              {
                path: examiner.certification.writing.details.path,
                breadcrumb: { text: 'Writing Certification Details', icon: 'pencil' },
                lazy: () => import('./certification/writing/writing-certification-details'),
              },
            ],
          },
          {
            path: examiner.certification.lifeSkills.path,
            abstract: true,
            children: [
              {
                path: examiner.certification.lifeSkills.add.path,
                breadcrumb: { text: 'Add Life Skills Certification', icon: 'plus' },
                lazy: () => import('./certification/life-skills/life-skills-certification-add'),
              },
              {
                path: examiner.certification.lifeSkills.details.path,
                breadcrumb: {
                  text: 'Life Skills Certification Details',
                  icon: 'chart line',
                },
                lazy: () => import('./certification/life-skills/life-skills-certification-details'),
              },
            ],
          },
        ],
      },
      {
        path: examiner.odpAssessment.path,
        breadcrumb: { text: 'ODP Assessment', icon: 'book' },
        abstract: true,
        children: [
          {
            index: true,
            lazy: () => import('./odp-assessment/odp-assessment-list'),
          },
          {
            path: examiner.odpAssessment.add.path,
            breadcrumb: { text: 'Add ODP Assessment', icon: 'plus' },
            lazy: () => import('./odp-assessment/odp-assessment-add'),
          },
          {
            path: examiner.odpAssessment.details.path,
            breadcrumb: { text: 'ODP Assessment Details', icon: 'pencil' },
            lazy: () => import('./odp-assessment/odp-assessment-details'),
          },
        ],
      },
      {
        path: examiner.trainingAndStandardisation.path,
        breadcrumb: { text: 'Training & Standardisation', icon: 'graduation cap' },
        abstract: true,
        children: [
          {
            index: true,
            lazy: () => import('./training-standardisation/TrainingStandardisationList'),
          },
          {
            path: examiner.trainingAndStandardisation.add.path,
            breadcrumb: { text: 'Add Training & Standardisation', icon: 'plus' },
            lazy: () => import('./training-standardisation/training-standardisation-add'),
          },
          {
            path: examiner.trainingAndStandardisation.details.path,
            breadcrumb: { text: 'Training & Standardisation Details', icon: 'pencil' },
            lazy: () => import('./training-standardisation/training-standardisation-details'),
          },
        ],
      },
      {
        path: examiner.monitoring.path,
        breadcrumb: { text: 'Monitoring', icon: 'eye' },
        abstract: true,
        children: [
          {
            // Tabs configuration
            element: (
              <ProtectedPage {...examiner.monitoring}>
                <Monitoring />
              </ProtectedPage>
            ),
            children: [
              { index: true, element: <Navigate to="speaking" /> },
              { path: 'speaking', element: <SpeakingMonitoringList /> },
              {
                path: 'odp',
                element: (
                  <ProtectedExaminerPage requiresAny={[Permission.ViewOdp]}>
                    <OdpMonitoringList />
                  </ProtectedExaminerPage>
                ),
              },
              {
                path: 'eorspeaking',
                element: (
                  <ProtectedExaminerPage requiresAny={[Permission.ViewEorMonitoringResults]}>
                    <EorSpeakingMonitoringList />
                  </ProtectedExaminerPage>
                ),
              },
              { path: 'onlinespeaking', element: <OnlineSpeakingMonitoringList /> },
              {
                path: 'osm',
                element: (
                  <ProtectedExaminerPage requiresAnyRole={[ExaminerRoleEnum.IELTSWritingStandard]}>
                    <OsmMonitoringList />
                  </ProtectedExaminerPage>
                ),
              },
              { path: 'lifeskills', element: <LifeSkillsMonitoringList /> },
            ],
          },
          {
            path: 'lifeskills/add',
            breadcrumb: { text: 'Add Life Skills Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/life-skills/life-skills-monitoring-add'),
          },
          {
            path: 'lifeskills/:monitoringId',
            breadcrumb: { text: 'Life Skills Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/life-skills/life-skills-monitoring-details'),
          },
          {
            path: 'eorspeaking/add',
            breadcrumb: { text: 'Add EOR Speaking Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/eor-speaking/eor-monitoring-add'),
          },
          {
            path: 'eorspeaking/:monitoringGroupId/add',
            breadcrumb: { text: 'Add EOR Speaking Monitoring Stage', icon: 'plus' },
            lazy: () => import('./monitoring/eor-speaking/eor-monitoring-add-stage'),
          },
          {
            path: 'eorspeaking/:monitoringGroupId',
            breadcrumb: { text: 'EOR Speaking Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/eor-speaking/eor-monitoring-details'),
          },
          {
            path: 'onlinespeaking/add',
            breadcrumb: { text: 'Add Online Speaking Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/online-speaking/online-speaking-monitoring-add'),
          },
          {
            path: 'onlinespeaking/archive',
            breadcrumb: { text: 'Archive Online Speaking Monitoring', icon: 'archive' },
            lazy: () => import('./monitoring/online-speaking/online-speaking-monitoring-archive'),
          },
          {
            path: 'onlinespeaking/:monitoringId',
            breadcrumb: { text: 'Online Speaking Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/online-speaking/online-speaking-monitoring-details'),
          },
          {
            path: 'osm/add',
            breadcrumb: { text: 'Add OSM Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/osm/osm-monitoring-add'),
          },
          {
            path: 'osm/archive',
            breadcrumb: { text: 'Archive OSM Monitoring', icon: 'archive' },
            lazy: () => import('./monitoring/osm/osm-monitoring-archive'),
          },
          {
            path: 'osm/:monitoringId',
            breadcrumb: { text: 'OSM Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/osm/osm-monitoring-details'),
          },
          {
            path: 'odp/add',
            breadcrumb: { text: 'Add ODP Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/odp/odp-monitoring-add'),
          },
          {
            path: 'odp/:odpMonitoringId',
            breadcrumb: { text: 'ODP Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/odp/odp-monitoring-details'),
          },
          {
            path: 'speaking/add',
            breadcrumb: { text: 'Add Speaking Monitoring', icon: 'plus' },
            lazy: () => import('./monitoring/speaking/speaking-monitoring-add'),
          },
          {
            path: 'speaking/:monitoringId',
            breadcrumb: { text: 'Speaking Monitoring Details', icon: 'eye' },
            lazy: () => import('./monitoring/speaking/speaking-monitoring-details'),
          },
          { path: '*', element: <NotFound /> },
        ],
      },
      {
        path: examiner.disciplinary.path,
        breadcrumb: { text: 'Disciplinary', icon: 'balance scale' },
        abstract: true,
        children: [
          {
            index: true,
            lazy: () => import('./disciplinary'),
          },
          {
            path: examiner.disciplinary.add.path,
            breadcrumb: { text: 'Add Disciplinary Record', icon: 'plus' },
            lazy: () => import('./disciplinary/add'),
          },
        ],
      },
      {
        path: examiner.documents.path,
        breadcrumb: { text: 'Documents', icon: 'file text outline' },
        abstract: true,
        children: [
          {
            lazy: () => import('./documents'),
            children: [
              { index: true, element: <Navigate to="psn" replace /> },
              { path: 'psn', lazy: () => import('./documents/psn') },
              { path: 'invitation', lazy: () => import('./documents/invitation') },
              { path: 'copcu', lazy: () => import('./documents/copcu') },
              { path: 'other', lazy: () => import('./documents/other') },
            ],
          },
          {
            path: examiner.documents.add.path,
            breadcrumb: { text: 'Add Document', icon: 'plus' },
            lazy: () => import('./documents/add'),
          },
        ],
      },
      {
        path: examiner.communication.path,
        breadcrumb: { text: 'Communication', icon: 'mail' },
        lazy: () => import('./communication'),
      },
    ],
  },
];
